import { useEffect, useRef, useState, useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import { useNarrative } from '../../contexts/NarrativeContext'
import {
  loadAllTrendsForLocation,
  prepareEnrollmentTrendData,
  prepareInstitutionTrendData,
  preparePLETrendData,
} from '../../utils/trendsDataUtils'

// Import bare chart components (for narrative embedding)
import InstitutionDistributionChart from './charts/bare/InstitutionDistributionChart'
import InfrastructureStatusChart from './charts/bare/InfrastructureStatusChart'
import TeacherQualificationsChart from './charts/bare/TeacherQualificationsChart'
import EducationRatiosChart from './charts/bare/EducationRatiosChart'
import GenderParityChart from './charts/bare/GenderParityChart'
import LocationRankingChart from './charts/bare/LocationRankingChart'
import EducationOverviewChart from './charts/bare/EducationOverviewChart'

// Import trend chart components
import EnrollmentTrendsChart from './charts/trends/EnrollmentTrendsChart'
import InstitutionGrowthChart from './charts/trends/InstitutionGrowthChart'
import PLEPerformanceTrendsChart from './charts/trends/PLEPerformanceTrendsChart'

/**
 * Hybrid Education Narrative Component
 *
 * Combines HTML-based editorial narratives with React chart components:
 * - Loads rich HTML narratives from /public/narratives/html/
 * - Mounts React charts into designated mount points using data-chart attributes
 * - Falls back gracefully to template-based content when narratives missing
 * - Integrates with React state for location/filter changes
 */
export default function EducationNarrativeHybrid() {
  const {
    currentLocation,
    facilityData: contextFacilityData,
    allFacilities,
  } = useNarrative()

  const [htmlContent, setHtmlContent] = useState('')
  const [hasNarrative, setHasNarrative] = useState(false)
  const [trendData, setTrendData] = useState(null)
  const [_institutionFilter, setInstitutionFilter] = useState('primary') // 'primary' | 'all'

  const contentRef = useRef(null)
  const chartRootsRef = useRef(new Map()) // Track React roots for cleanup

  // Use facility data from context (already aggregated)
  const facilityData = contextFacilityData

  // Filter facilities for current location (for map display)
  const locationFacilities = useMemo(() => {
    if (!currentLocation || !allFacilities?.education) return []

    return allFacilities.education.filter(school => {
      if (currentLocation.type === 'district') {
        return school.district === currentLocation.name
      } else if (currentLocation.type === 'subcounty') {
        return (
          school.subcounty === currentLocation.name &&
          school.district === currentLocation.parent?.name
        )
      } else if (currentLocation.type === 'parish') {
        return school.parish === currentLocation.name
      }
      return false
    })
  }, [currentLocation, allFacilities])

  // Log when facility data changes
  useEffect(() => {
    if (facilityData) {
      console.log('=== Education Facility Data Loaded ===')
      console.log('Facility data:', facilityData)
      console.log(
        'Has totalInstitutions?',
        'totalInstitutions' in (facilityData.metrics || {})
      )
      console.log(
        'Has totalFacilities?',
        'totalFacilities' in (facilityData.metrics || {})
      )
      console.log(
        'Has educationRatios?',
        'educationRatios' in (facilityData.metrics || {})
      )
      console.log(
        'Has serviceCategories?',
        'serviceCategories' in (facilityData.metrics || {})
      )
    }
  }, [facilityData])

  // Load trend data
  useEffect(() => {
    if (!currentLocation?.id || !currentLocation?.code) return

    const loadTrends = async () => {
      try {
        // Extract district name from location
        const district = currentLocation.id.startsWith('d-kayunga')
          ? 'Kayunga'
          : currentLocation.id.startsWith('d-masindi')
            ? 'Masindi'
            : null

        if (!district) {
          console.warn('Unknown district for trend data:', currentLocation.id)
          return
        }

        const trends = await loadAllTrendsForLocation(
          currentLocation.code,
          district
        )

        // Prepare trend data for charts
        const preparedTrends = {
          enrollment: null,
          institutionGrowth: null,
          plePerformance: null,
        }

        // Prepare enrollment data (district-specific)
        if (
          trends.secondaryEnrollment &&
          trends.secondaryEnrollment.length > 0
        ) {
          preparedTrends.enrollment = prepareEnrollmentTrendData(
            trends.secondaryEnrollment
          )
        } else if (
          trends.primaryEnrollment &&
          trends.primaryEnrollment.length > 0
        ) {
          preparedTrends.enrollment = prepareEnrollmentTrendData(
            trends.primaryEnrollment
          )
        }

        // Prepare institution growth data
        if (trends.institutionCount && trends.institutionCount.length > 0) {
          preparedTrends.institutionGrowth = prepareInstitutionTrendData(
            trends.institutionCount
          )
        }

        // Prepare PLE performance data
        if (trends.plePerformance && trends.plePerformance.length > 0) {
          preparedTrends.plePerformance = preparePLETrendData(
            trends.plePerformance
          )
        }

        setTrendData(preparedTrends)
      } catch (error) {
        console.error('Error loading trend data:', error)
      }
    }

    loadTrends()
  }, [currentLocation])

  // Load HTML narrative content
  useEffect(() => {
    if (!currentLocation?.id) return

    const loadNarrative = async () => {
      const locationId = currentLocation.id
      const simplePath = `/narratives/html/${locationId}-education.html`
      const narrativePath = `/narratives/html/${locationId}-education-template.html`
      const fallbackPath = `/narratives/html/${locationId}-education-fallback.html`

      try {
        // Try simple path first (e.g., d-kayunga-education.html)
        let response = await fetch(simplePath)
        if (response.ok) {
          const html = await response.text()
          setHtmlContent(html)
          setHasNarrative(true)
          return
        }

        // Try to load rich narrative template
        response = await fetch(narrativePath)
        if (response.ok) {
          const html = await response.text()
          setHtmlContent(html)
          setHasNarrative(true)
          return
        }

        // Fall back to template-based content
        const fallbackResponse = await fetch(fallbackPath)
        if (fallbackResponse.ok) {
          const html = await fallbackResponse.text()
          setHtmlContent(html)
          setHasNarrative(false)
          return
        }

        // If all fail, show minimal content
        setHtmlContent(
          '<div class="text-center text-gray-500 py-12">No narrative content available for this location.</div>'
        )
        setHasNarrative(false)
      } catch (error) {
        console.error('Error loading narrative HTML:', error)
        setHtmlContent(
          '<div class="text-center text-gray-500 py-12">Error loading narrative content.</div>'
        )
        setHasNarrative(false)
      }
    }

    loadNarrative()
  }, [currentLocation])

  // Mount React charts into HTML
  useEffect(() => {
    if (!htmlContent || !facilityData || !contentRef.current) return

    // Unmount all existing charts first (to handle data changes)
    chartRootsRef.current.forEach(root => {
      root.unmount()
    })
    chartRootsRef.current.clear()

    const mountCharts = () => {
      const chartMounts = contentRef.current.querySelectorAll('[data-chart]')
      console.log('=== Chart Mounting Debug ===')
      console.log('Found chart mount points:', chartMounts.length)
      console.log('Has facility data:', !!facilityData)
      console.log('Has trend data:', !!trendData)

      chartMounts.forEach(mountElement => {
        const chartType = mountElement.dataset.chart
        console.log('Attempting to mount chart:', chartType)

        let chartComponent = null

        // Map chart types to BARE components (no wrappers/titles)
        switch (chartType) {
          // Overview chart (stats + map)
          case 'education-overview':
            chartComponent = (
              <EducationOverviewChart
                data={{
                  metrics: facilityData.metrics,
                  facilities: locationFacilities,
                  location: currentLocation,
                }}
              />
            )
            break

          // Snapshot data charts
          case 'institution-distribution':
            chartComponent = (
              <InstitutionDistributionChart
                data={facilityData.metrics.institutionDistribution}
              />
            )
            break

          case 'infrastructure-status':
          case 'infrastructure-availability':
            chartComponent = (
              <InfrastructureStatusChart
                data={facilityData.metrics.infrastructureAvailability}
              />
            )
            break

          case 'teacher-qualifications':
            chartComponent = (
              <TeacherQualificationsChart
                data={facilityData.metrics.teacherQualifications}
              />
            )
            break

          case 'resource-ratios':
            chartComponent = (
              <EducationRatiosChart
                data={facilityData.metrics.educationRatios}
              />
            )
            break

          case 'gender-parity':
            chartComponent = (
              <GenderParityChart data={facilityData.metrics.genderParity} />
            )
            break

          case 'location-ranking':
          case 'subcounty-comparison':
            if (
              facilityData.subcountyMetrics &&
              facilityData.subcountyMetrics.length > 0
            ) {
              chartComponent = (
                <LocationRankingChart
                  data={{
                    rankings: facilityData.subcountyMetrics,
                    context: `Comparing ${facilityData.subcountyMetrics.length} sub-locations`,
                  }}
                />
              )
            }
            break

          // Trend data charts
          case 'enrollment-trends':
            if (trendData?.enrollment) {
              chartComponent = (
                <EnrollmentTrendsChart data={trendData.enrollment} />
              )
            }
            break

          case 'institution-growth':
            if (trendData?.institutionGrowth) {
              chartComponent = (
                <InstitutionGrowthChart data={trendData.institutionGrowth} />
              )
            }
            break

          case 'ple-performance-trends':
          case 'ple-performance':
            if (trendData?.plePerformance) {
              chartComponent = (
                <PLEPerformanceTrendsChart data={trendData.plePerformance} />
              )
            }
            break

          default:
            console.warn(`Unknown chart type: ${chartType}`)
        }

        if (chartComponent) {
          try {
            // Create a new root and render the chart
            const root = createRoot(mountElement)
            root.render(chartComponent)
            chartRootsRef.current.set(chartType, root)
            console.log('  ✓ Successfully mounted:', chartType)
          } catch (error) {
            console.error(`  ✗ Error mounting chart ${chartType}:`, error)
          }
        } else {
          console.log('  ✗ No component created for:', chartType)
        }
      })
    }

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(mountCharts, 100)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    htmlContent,
    facilityData,
    trendData,
    currentLocation,
    locationFacilities,
  ])

  // Cleanup chart roots on unmount
  useEffect(() => {
    return () => {
      chartRootsRef.current.forEach(root => {
        root.unmount()
      })
      chartRootsRef.current.clear()
    }
  }, [])

  // Handle institution filter toggle
  const _handleFilterChange = filter => {
    setInstitutionFilter(filter)

    // Unmount existing charts so they re-render with new data
    chartRootsRef.current.forEach(root => {
      root.unmount()
    })
    chartRootsRef.current.clear()
  }

  console.log('Has Narrative: ', hasNarrative)

  if (!currentLocation) {
    return (
      <div className="text-center text-gray-500 py-12">
        Please select a location to view education narrative.
      </div>
    )
  }

  return (
    <div className="education-narrative-hybrid">
      {/* HTML Narrative Content with Chart Mount Points */}
      <div
        ref={contentRef}
        className="narrative-content"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

      {/* Loading State */}
      {!facilityData && (
        <div className="text-center text-gray-500 py-12">
          Loading education data...
        </div>
      )}
    </div>
  )
}
